import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Hexagon, Settings, Plus, Pencil, Trash2, ChevronDown, ChevronUp,
  Users, Building2, User, CheckSquare, Loader2, ArrowLeft, Save,
  UserCheck, AlertCircle, Clock, Mail, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import ApprovalGroupsManager from '../components/ApprovalGroupsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Approval level type options
const APPROVER_TYPES = [
  { value: 'DEPARTMENT_MANAGER', label: 'Department manager', description: 'Manager of the employee\'s department' },
  { value: 'REPORTING_MANAGER', label: 'Reporting manager', description: 'Employee\'s direct line manager' },
  { value: 'SPECIFIC_USER', label: 'A specific person', description: 'One named person must approve' },
  { value: 'GROUP', label: 'An approval group', description: 'Any one member of the group can approve — first to respond wins' },
  { value: 'ROLE', label: 'Anyone with a role', description: 'Anyone with a specific role (e.g. admin)' },
];

// Scope options
const SCOPE_OPTIONS = [
  { value: 'COMPANY', label: 'Company-wide', icon: Building2, description: 'Applies to all employees' },
  { value: 'DEPARTMENT', label: 'Department', icon: Users, description: 'Applies to a specific department' },
  { value: 'USER', label: 'Individual', icon: User, description: 'Applies to a specific employee' },
];

const ApprovalPoliciesPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Data
  const [policies, setPolicies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // UI state
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Form state
  const [policyForm, setPolicyForm] = useState({
    name: '',
    description: '',
    scope: 'COMPANY',
    department_id: '',
    user_id: '',
    is_active: true,
    is_default: false,
    priority: 100,
    levels: []
  });
  
  const token = localStorage.getItem('token');
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || 
                  user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  
  useEffect(() => {
    fetchData();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link from an employee record: ?new_user=<id> opens a pre-scoped individual multi-step flow
  useEffect(() => {
    const nu = searchParams.get('new_user');
    if (nu && !loading && employees.length && !showPolicyDialog) {
      const emp = employees.find((e) => String(e.id) === String(nu));
      setEditingPolicy(null);
      setPolicyForm({
        name: emp ? `${emp.first_name} ${emp.last_name} \u2014 approval flow` : 'Individual approval flow',
        description: '', scope: 'USER', department_id: '', user_id: String(nu),
        is_active: true, is_default: false, priority: 50,
        levels: [
          { level_order: 1, name: 'First approval', approver_type: 'DEPARTMENT_MANAGER', approver_role: null, approver_user_id: null, approver_group_id: null, backup_approver_user_id: null, auto_approve_after_days: null },
          { level_order: 2, name: 'Second approval', approver_type: 'SPECIFIC_USER', approver_role: null, approver_user_id: null, approver_group_id: null, backup_approver_user_id: null, auto_approve_after_days: null },
        ],
      });
      setShowPolicyDialog(true);
      const sp = new URLSearchParams(searchParams); sp.delete('new_user'); setSearchParams(sp, { replace: true });
    }
  // `showPolicyDialog` is excluded deliberately — re-running when the dialog opens would reopen it. `setSearchParams` is stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, employees]);

  // Deep link to edit an existing policy: ?edit=<policyId>
  useEffect(() => {
    const ed = searchParams.get('edit');
    if (ed && !loading && policies.length && !showPolicyDialog) {
      const pol = policies.find((pp) => String(pp.id) === String(ed));
      if (pol) {
        handleEditPolicy(pol);
        const sp = new URLSearchParams(searchParams); sp.delete('edit'); setSearchParams(sp, { replace: true });
      }
    }
  // `showPolicyDialog` is excluded deliberately — re-running when the dialog opens would reopen it. `setSearchParams` is stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, policies]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch approval policies
      const policiesRes = await fetch(`${API_URL}/api/approval-policies?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const policiesData = await policiesRes.json();
      setPolicies(policiesData);
      
      // Fetch departments for scope selection
      const deptRes = await fetch(`${API_URL}/api/departments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const deptData = await deptRes.json();
      setDepartments(deptData);
      
      // Fetch employees for specific user selection
      const empRes = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const empData = await empRes.json();
      setEmployees(empData.filter(e => e.is_active));

      // Approval groups, for steps signed off by "any of the directors". Only groups
      // with somebody in them who can actually approve are offered — the backend
      // refuses the others anyway, and a dropdown that lets you pick a dead end is
      // just a slower way of showing an error.
      try {
        const grpRes = await fetch(`${API_URL}/api/approval-groups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const grpData = await grpRes.json();
        setGroups(Array.isArray(grpData) ? grpData : []);
      } catch (err) {
        console.error('Error fetching approval groups:', err);
        setGroups([]);
      }

      // Get pending count for sidebar
      try {
        const pendingRes = await fetch(`${API_URL}/api/leave/requests?status_filter=pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const pendingData = await pendingRes.json();
        setPendingCount(Array.isArray(pendingData) ? pendingData.length : 0);
      } catch (err) {
        console.error('Error fetching pending count:', err);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load approval policies');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setPolicyForm({
      name: '',
      description: '',
      scope: 'COMPANY',
      department_id: '',
      user_id: '',
      is_active: true,
      is_default: false,
      priority: 100,
      levels: [
        { level_order: 1, name: 'Manager approval', approver_type: 'DEPARTMENT_MANAGER', approver_role: null, approver_user_id: null, approver_group_id: null, backup_approver_user_id: null, auto_approve_after_days: null }
      ]
    });
    setShowPolicyDialog(true);
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      name: policy.name,
      description: policy.description || '',
      scope: policy.scope,
      department_id: policy.department_id || '',
      user_id: policy.user_id || '',
      is_active: policy.is_active,
      is_default: policy.is_default,
      priority: policy.priority,
      levels: policy.levels.map(l => ({
        id: l.id,
        level_order: l.level_order,
        name: l.name || '',
        approver_type: l.approver_type,
        approver_role: l.approver_role,
        approver_user_id: l.approver_user_id,
        approver_group_id: l.approver_group_id,
        backup_approver_user_id: l.backup_approver_user_id,
        auto_approve_after_days: l.auto_approve_after_days
      }))
    });
    setShowPolicyDialog(true);
  };
  
  const handleSavePolicy = async () => {
    if (!policyForm.name.trim()) {
      toast.error('Policy name is required');
      return;
    }
    if (policyForm.scope === 'DEPARTMENT' && !policyForm.department_id) {
      toast.error('Please select a department');
      return;
    }
    if (policyForm.scope === 'USER' && !policyForm.user_id) {
      toast.error('Please select an employee');
      return;
    }
    if (policyForm.levels.length === 0) {
      toast.error('At least one approval step is required');
      return;
    }
    const missingPerson = policyForm.levels.findIndex(
      (l) => l.approver_type === 'SPECIFIC_USER' && !l.approver_user_id);
    if (missingPerson !== -1) {
      toast.error(`Step ${missingPerson + 1}: choose a person`);
      return;
    }
    const missingGroup = policyForm.levels.findIndex(
      (l) => l.approver_type === 'GROUP' && !l.approver_group_id);
    if (missingGroup !== -1) {
      toast.error(`Step ${missingGroup + 1}: choose an approval group`);
      return;
    }
    const missingRole = policyForm.levels.findIndex(
      (l) => l.approver_type === 'ROLE' && !l.approver_role);
    if (missingRole !== -1) {
      toast.error(`Step ${missingRole + 1}: choose a role`);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: policyForm.name,
        description: policyForm.description || null,
        scope: policyForm.scope,
        department_id: policyForm.scope === 'DEPARTMENT' ? policyForm.department_id : null,
        user_id: policyForm.scope === 'USER' ? policyForm.user_id : null,
        is_active: policyForm.is_active,
        is_default: policyForm.is_default,
        priority: policyForm.priority,
        levels: policyForm.levels.map((l, idx) => ({
          level_order: idx + 1,
          name: l.name || `Step ${idx + 1}`,
          approver_type: l.approver_type,
          approver_role: l.approver_type === 'ROLE' ? l.approver_role : null,
          approver_user_id: l.approver_type === 'SPECIFIC_USER' ? l.approver_user_id : null,
          approver_group_id: l.approver_type === 'GROUP' ? l.approver_group_id : null,
          backup_approver_user_id: l.backup_approver_user_id || null,
          auto_approve_after_days: l.auto_approve_after_days || null
        }))
      };
      
      // ONE request, whether creating or editing. The steps travel with the policy and
      // the backend replaces them in a single transaction.
      //
      // This used to fire a PUT, then a DELETE per existing step, then a POST per new
      // step — from the browser, with only the first reply checked. Half a dozen
      // unchecked round trips meant a failure part-way through (or closing the tab)
      // left the policy with missing or zero steps while still reporting success. A
      // policy with no steps quietly falls back to the default approver chain.
      const response = await fetch(
        editingPolicy
          ? `${API_URL}/api/approval-policies/${editingPolicy.id}`
          : `${API_URL}/api/approval-policies`,
        {
          method: editingPolicy ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        // FastAPI puts validation errors in an ARRAY under `detail`; handing that
        // straight to a toast makes React try to render an object and blanks the page.
        let detail;
        try {
          const err = await response.json();
          detail = Array.isArray(err?.detail)
            ? err.detail.map((d) => d?.msg).filter(Boolean).join('; ')
            : (typeof err?.detail === 'string' ? err.detail : null);
        } catch (_) { /* non-JSON error body */ }
        throw new Error(detail || `Failed to save policy (${response.status})`);
      }

      toast.success(editingPolicy ? 'Policy updated successfully' : 'Policy created successfully');
      setShowPolicyDialog(false);
      fetchData();
      
    } catch (err) {
      console.error('Error saving policy:', err);
      toast.error(err.message || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeletePolicy = async (policy) => {
    if (policy.is_default) {
      toast.error('Cannot delete the default policy. Set another policy as default first.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the policy "${policy.name}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/approval-policies/${policy.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete policy');
      }
      
      toast.success('Policy deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting policy:', err);
      toast.error(err.message || 'Failed to delete policy');
    }
  };
  
  const addApprovalLevel = () => {
    const newOrder = policyForm.levels.length + 1;
    setPolicyForm(prev => ({
      ...prev,
      levels: [...prev.levels, {
        level_order: newOrder,
        name: `Step ${newOrder}`,
        approver_type: 'DEPARTMENT_MANAGER',
        approver_role: null,
        approver_user_id: null,
        approver_group_id: null,
        backup_approver_user_id: null,
        auto_approve_after_days: null
      }]
    }));
  };

  const removeApprovalLevel = (index) => {
    if (policyForm.levels.length <= 1) {
      toast.error('At least one approval step is required');
      return;
    }
    setPolicyForm(prev => ({
      ...prev,
      levels: prev.levels.filter((_, i) => i !== index).map((l, i) => ({ ...l, level_order: i + 1 }))
    }));
  };
  
  const updateApprovalLevel = (index, field, value) => {
    setPolicyForm(prev => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === index ? { ...l, [field]: value } : l)
    }));
  };
  
  const moveLevel = (index, direction) => {
    const newLevels = [...policyForm.levels];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newLevels.length) return;
    
    [newLevels[index], newLevels[targetIndex]] = [newLevels[targetIndex], newLevels[index]];
    newLevels.forEach((l, i) => l.level_order = i + 1);
    
    setPolicyForm(prev => ({ ...prev, levels: newLevels }));
  };
  
  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'COMPANY': return <Building2 className="w-4 h-4" />;
      case 'DEPARTMENT': return <Users className="w-4 h-4" />;
      case 'USER': return <User className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };
  
  const getApproverTypeLabel = (type) => {
    const option = APPROVER_TYPES.find(t => t.value === type);
    return option ? option.label : type;
  };
  
  const renderPolicyDialog = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="policy-dialog">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-heading font-bold">
            {editingPolicy ? 'Edit Approval Policy' : 'Create Approval Policy'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure who needs to approve leave requests and in what order
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Policy Name *</Label>
              <Input
                id="name"
                value={policyForm.name}
                onChange={(e) => setPolicyForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Standard Approval"
                data-testid="policy-name-input"
              />
            </div>
            <div>
              <Label htmlFor="priority">Priority (lower = higher priority)</Label>
              <Input
                id="priority"
                type="number"
                value={policyForm.priority}
                onChange={(e) => setPolicyForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 100 }))}
                data-testid="policy-priority-input"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={policyForm.description}
              onChange={(e) => setPolicyForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of when this policy applies"
              data-testid="policy-description-input"
            />
          </div>
          
          {/* Scope */}
          <div>
            <Label>Applies To</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {SCOPE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPolicyForm(prev => ({ ...prev, scope: option.value, department_id: '', user_id: '' }))}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    policyForm.scope === option.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`scope-${option.value.toLowerCase()}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <option.icon className="w-4 h-4" />
                    <span className="font-medium">{option.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
          
          {/* Scope-specific selection */}
          {policyForm.scope === 'DEPARTMENT' && (
            <div>
              <Label htmlFor="department">Select Department *</Label>
              <select
                id="department"
                className="input-hive w-full mt-1"
                value={policyForm.department_id}
                onChange={(e) => setPolicyForm(prev => ({ ...prev, department_id: e.target.value }))}
                data-testid="policy-department-select"
              >
                <option value="">Choose a department...</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {policyForm.scope === 'USER' && (
            <div>
              <Label htmlFor="user">Select Employee *</Label>
              <select
                id="user"
                className="input-hive w-full mt-1"
                value={policyForm.user_id}
                onChange={(e) => setPolicyForm(prev => ({ ...prev, user_id: e.target.value }))}
              >
                <option value="">Choose an employee...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Approval Levels */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label>Approval Levels</Label>
                <p className="text-xs text-muted-foreground">Add multiple levels for sequential approval</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addApprovalLevel}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Level
              </Button>
            </div>
            
            <div className="space-y-3">
              {policyForm.levels.map((level, index) => (
                <div key={index} className="border border-border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <Input
                        value={level.name}
                        onChange={(e) => updateApprovalLevel(index, 'name', e.target.value)}
                        className="w-48"
                        placeholder="Level name"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => moveLevel(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => moveLevel(index, 'down')}
                        disabled={index === policyForm.levels.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeApprovalLevel(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Who can approve?</Label>
                      <select
                        className="input-hive w-full mt-1 text-sm"
                        value={level.approver_type}
                        onChange={(e) => updateApprovalLevel(index, 'approver_type', e.target.value)}
                      >
                        {APPROVER_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {level.approver_type === 'SPECIFIC_USER' && (
                      <div>
                        <Label className="text-xs">Select Person *</Label>
                        <select
                          className="input-hive w-full mt-1 text-sm"
                          value={level.approver_user_id || ''}
                          onChange={(e) => updateApprovalLevel(index, 'approver_user_id', e.target.value)}
                        >
                          <option value="">Choose...</option>
                          {employees.filter(e => e.role !== 'employee').map(emp => (
                            <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {level.approver_type === 'GROUP' && (
                      <div>
                        <Label className="text-xs">Which group? *</Label>
                        <select
                          className="input-hive w-full mt-1 text-sm"
                          value={level.approver_group_id || ''}
                          onChange={(e) => updateApprovalLevel(index, 'approver_group_id', e.target.value || null)}
                          data-testid={`level-${index}-group`}
                        >
                          <option value="">Choose...</option>
                          {groups.map(g => (
                            <option key={g.id} value={g.id} disabled={!g.usable_members}>
                              {g.name}
                              {g.usable_members
                                ? ` (${g.usable_members} ${g.usable_members === 1 ? 'approver' : 'approvers'})`
                                : ' — nobody in it can approve'}
                            </option>
                          ))}
                        </select>
                        {groups.length === 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            No approval groups yet — create one in the Approval groups
                            section below.
                          </p>
                        )}
                        {level.approver_group_id && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Everyone in the group is asked. The first to respond decides
                            it, and the others are told it's been handled.
                          </p>
                        )}
                      </div>
                    )}

                    {level.approver_type === 'ROLE' && (
                      <div>
                        <Label className="text-xs">Select Role *</Label>
                        <select
                          className="input-hive w-full mt-1 text-sm"
                          value={level.approver_role || ''}
                          onChange={(e) => updateApprovalLevel(index, 'approver_role', e.target.value)}
                        >
                          <option value="">Choose...</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    )}

                    <div>
                      <Label className="text-xs">Backup Approver (optional)</Label>
                      <select
                        className="input-hive w-full mt-1 text-sm"
                        value={level.backup_approver_user_id || ''}
                        onChange={(e) => updateApprovalLevel(index, 'backup_approver_user_id', e.target.value || null)}
                      >
                        <option value="">None</option>
                        {employees.filter(e => e.role !== 'employee').map(emp => (
                          <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Reminders used to be configured here, per policy — three controls that
              were stored, returned by the API, and read by nothing. Nothing had ever
              sent a reminder. They now work, but platform-wide and set by us:
              Superadmin → Email Reminders. See the liveness spec in
              DESIGN-org-and-approvals.md for why they aren't a customer setting.
              As of 30/07/2026 the reminder_* fields are no longer sent on save and no
              longer returned by the API either. The columns still exist and keep their
              old values, but a field that's accepted and ignored is a trap: it invites
              someone to wire a control to it and wonder why nothing happens. */}
          <div className="border-t border-border pt-4">
            <p className="text-sm text-muted-foreground">
              If nobody responds, we'll chase the approver automatically and then pass
              the request to an admin. Nothing is ever approved for you.
            </p>
          </div>

          {/* Status */}
          <div className="flex items-center gap-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={policyForm.is_active}
                onCheckedChange={(checked) => setPolicyForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="is_default"
                checked={policyForm.is_default}
                onCheckedChange={(checked) => setPolicyForm(prev => ({ ...prev, is_default: checked }))}
              />
              <Label htmlFor="is_default">Set as default for {policyForm.scope.toLowerCase()}</Label>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={() => setShowPolicyDialog(false)} disabled={saving} data-testid="policy-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSavePolicy} disabled={saving} data-testid="policy-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {editingPolicy ? 'Update Policy' : 'Create Policy'}
          </Button>
        </div>
      </div>
    </div>
  );
  
  const renderPolicyCard = (policy) => {
    const isExpanded = expandedPolicy === policy.id;
    
    return (
      <div key={policy.id} className="card-hive overflow-hidden" data-testid={`policy-card-${policy.id}`}>
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => setExpandedPolicy(isExpanded ? null : policy.id)}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${policy.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
              {getScopeIcon(policy.scope)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium">{policy.name}</h3>
                {policy.is_default && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-700">Default</span>
                )}
                {!policy.is_active && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">Inactive</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {policy.scope === 'COMPANY' && 'Company-wide policy'}
                {policy.scope === 'DEPARTMENT' && `Department: ${policy.department_name || 'Unknown'}`}
                {policy.scope === 'USER' && `Employee: ${policy.user_name || 'Unknown'}`}
                {' • '}{policy.levels.length} approval level{policy.levels.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditPolicy(policy); }} data-testid={`edit-policy-${policy.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeletePolicy(policy); }} className="text-red-500 hover:text-red-700" data-testid={`delete-policy-${policy.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        
        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-border pt-4">
            <p className="text-sm text-muted-foreground mb-4">{policy.description || 'No description'}</p>
            
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <CheckSquare className="w-4 h-4" /> Approval Flow
            </h4>
            
            <div className="flex items-start gap-4 flex-wrap">
              {policy.levels.sort((a, b) => a.level_order - b.level_order).map((level, idx) => (
                <React.Fragment key={level.id}>
                  <div className="flex-shrink-0 bg-muted/50 rounded-lg p-3 border border-border min-w-[180px]">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                        {level.level_order}
                      </span>
                      <span className="font-medium text-sm">{level.name || `Level ${level.level_order}`}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{getApproverTypeLabel(level.approver_type)}</p>
                    {/* A group step names the group, not a person — the whole point is
                        that no single person holds it. */}
                    {level.approver_group_name ? (
                      <p className="text-xs mt-1">
                        <Users className="w-3 h-3 inline mr-1" />
                        {level.approver_group_name}
                        <span className="text-muted-foreground"> — any one member</span>
                      </p>
                    ) : level.approver_user_name && (
                      <p className="text-xs mt-1"><UserCheck className="w-3 h-3 inline mr-1" />{level.approver_user_name}</p>
                    )}
                    {level.backup_approver_user_name && (
                      <p className="text-xs text-muted-foreground mt-1">Backup: {level.backup_approver_user_name}</p>
                    )}
                  </div>
                  {idx < policy.levels.length - 1 && (
                    <div className="flex items-center pt-6">
                      <div className="h-px w-8 bg-border"></div>
                      <span className="text-xs text-muted-foreground mx-1">then</span>
                      <div className="h-px w-8 bg-border"></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
              
              <div className="flex items-center pt-6">
                <div className="h-px w-8 bg-border"></div>
                <span className="text-xs text-muted-foreground mx-1">→</span>
                <div className="h-px w-8 bg-border"></div>
              </div>
              
              <div className="flex-shrink-0 bg-green-500/10 rounded-lg p-3 border border-green-500/20 min-w-[100px]">
                <p className="text-sm font-medium text-green-700">Approved</p>
              </div>
            </div>
            
            {/* Settings summary. The old "Reminders every Xh" badge was removed on
                30/07/2026 along with the dead per-policy reminder fields — it read from
                `policy.reminder_interval_hours`, which the API no longer returns, and it
                never described what actually happened anyway. Chasing is platform-wide:
                Superadmin → Email Reminders. */}
            <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" /> Approvers are chased automatically
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Priority: {policy.priority}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        pendingCount={pendingCount}
      />

      <main className="flex-1 lg:ml-64">
        <header className="bg-card border-b border-border sticky top-0 z-30">
          <div className="p-4 flex items-center gap-4">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Hexagon className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <Link to="/settings?tab=leave" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Approval Policies</h1>
                <p className="text-sm text-muted-foreground">Configure multi-level leave approval workflows</p>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !isAdmin ? (
            <div className="card-hive p-8 text-center">
              <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-heading font-bold mb-2">Admin Access Required</h2>
              <p className="text-muted-foreground">Only administrators can manage approval policies.</p>
            </div>
          ) : (
            <>
              {/* Info banner */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-700">How Approval Policies Work</h3>
                    <p className="text-sm text-blue-600 mt-1">
                      When an employee requests leave, the system finds the most specific policy that applies (Individual → Department → Company).
                      The request then flows through each approval level in order. All levels must approve for the leave to be granted.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Action bar */}
              {/* Approval groups — a named set of approvers, e.g. "Directors".
                  Sits above policies because a group is a building block you'd create
                  first and then select, rather than a competing concept. */}
              <div className="card-hive p-6 mb-8">
                <ApprovalGroupsManager people={employees} onChanged={fetchData} />
              </div>

              <div className="flex items-center justify-between mb-6">
                <div className="text-sm text-muted-foreground" data-testid="policies-count">
                  {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
                </div>
                <Button onClick={handleCreatePolicy} data-testid="create-policy-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Policy
                </Button>
              </div>
              
              {/* Policy list */}
              <div className="space-y-4">
                {policies.length === 0 ? (
                  <div className="card-hive p-8 text-center">
                    <CheckSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h2 className="text-xl font-heading font-bold mb-2">No Approval Policies</h2>
                    <p className="text-muted-foreground mb-4">Create your first approval policy to enable multi-level approval workflows.</p>
                    <Button onClick={handleCreatePolicy}>
                      <Plus className="w-4 h-4 mr-2" /> Create Policy
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Company policies */}
                    {policies.filter(p => p.scope === 'COMPANY').length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                          <Building2 className="w-4 h-4" /> Company-wide
                        </h3>
                        {policies.filter(p => p.scope === 'COMPANY').map(renderPolicyCard)}
                      </div>
                    )}
                    
                    {/* Department policies */}
                    {policies.filter(p => p.scope === 'DEPARTMENT').length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4" /> Department-specific
                        </h3>
                        {policies.filter(p => p.scope === 'DEPARTMENT').map(renderPolicyCard)}
                      </div>
                    )}
                    
                    {/* User policies */}
                    {policies.filter(p => p.scope === 'USER').length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                          <User className="w-4 h-4" /> Individual
                        </h3>
                        {policies.filter(p => p.scope === 'USER').map(renderPolicyCard)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      
      {/* Policy Dialog */}
      {showPolicyDialog && renderPolicyDialog()}
    </div>
  );
};

export default ApprovalPoliciesPage;
